/**
 * Phone push registration for Velora for Android. After a full sign-in the
 * app hands its Firebase token to Velora's server (identity
 * me/mobile-devices), which then sends category-only pushes ("New message,
 * open Velora"). On sign-out the token is retired first, so the next person
 * on a shared phone never gets the last person's notifications.
 */
import {
  MobileDeviceRegisterResponseSchema,
  OkSchema,
  type MobileDeviceRegisterResponse,
} from '@velora/contracts/identity';
import { api } from '../api.js';
import {
  androidAppVersion,
  isAndroidApp,
  requestAndroidPush,
  tellAndroidSession,
  type PushStatus,
} from './bridge.js';

let registeredToken: string | null = null;
let registeredFor: string | null = null;
let inFlight: Promise<PushStatus> | null = null;

/** Registers this phone for the signed-in person (once per person per page). */
export function registerAndroidPush(userId: string): Promise<PushStatus> {
  if (!isAndroidApp()) return Promise.resolve('unavailable');
  if (registeredFor === userId && registeredToken) return Promise.resolve('ok');
  if (inFlight) return inFlight;
  inFlight = (async (): Promise<PushStatus> => {
    const answer = await requestAndroidPush();
    if (answer.status !== 'ok' || !answer.token) return answer.status;
    const appVersion = androidAppVersion() ?? undefined;
    await api.request<MobileDeviceRegisterResponse>('/identity/me/mobile-devices', {
      method: 'POST',
      body: { platform: 'android', token: answer.token, ...(appVersion ? { appVersion } : {}) },
      schema: MobileDeviceRegisterResponseSchema,
    });
    registeredToken = answer.token;
    registeredFor = userId;
    return 'ok';
  })()
    .catch((): PushStatus => 'error')
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Before the session ends: retire this phone's token (while the session can
 * still say whose it is) and tell the app nobody is signed in.
 */
export async function androidSignOut(): Promise<void> {
  if (!isAndroidApp()) return;
  const token = registeredToken;
  registeredToken = null;
  registeredFor = null;
  if (token) {
    await api
      .request('/identity/me/mobile-devices/unregister', {
        method: 'POST',
        body: { token },
        schema: OkSchema,
      })
      .catch(() => undefined);
  }
  tellAndroidSession(false, true);
}

/** For tests. */
export function resetAndroidPush(): void {
  registeredToken = null;
  registeredFor = null;
  inFlight = null;
}
